import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sessionManager } from "@/utils/sessionManager";
import useSessionWithRetry from "@/hooks/useSessionWithRetry";

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(true);
  const { session, loading: sessionLoading } = useSessionWithRetry();

  useEffect(() => {
    // Wait for session to be available or fully failed
    if (sessionLoading) {
      return;
    }

    const handleAuthCallback = async () => {
      try {
        if (!session || !session.user) {
          console.error("No valid session found");
          toast.error("Authentication failed");
          navigate("/auth");
          return;
        }
        
        // Parse URL search params to check for redirect instructions
        const params = new URLSearchParams(location.search);
        const redirectTo = params.get('redirectTo');
        
        console.log("User authenticated:", session.user.email);
        
        // Verify that the user has a valid role
        const hasValidRole = await sessionManager.verifyUserRole(navigate);
        
        if (!hasValidRole) {
          // User will be silently logged out and redirected to home page by sessionManager
          return;
        }
        
        // Check user role
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .maybeSingle();
          
        if (roleError && roleError.code !== 'PGRST116') {
          console.error("Error fetching role:", roleError);
          toast.error("Error verifying your role");
          navigate("/auth");
          return;
        }
        
        console.log("Role data:", roleData);
        
        // Check if there's a saved redirect path in localStorage
        const savedRedirectPath = localStorage.getItem('redirectAfterLogin');
        console.log("Checking for saved redirect path:", savedRedirectPath);
        
        // Clear the redirect path from localStorage
        localStorage.removeItem('redirectAfterLogin');
        
        // Handle redirection based on saved path, role, or redirectTo parameter
        if (savedRedirectPath) {
          console.log(`Redirecting to saved path: ${savedRedirectPath}`);
          // Only redirect if the user has the correct role for the path
          if (savedRedirectPath.startsWith('/therapist/verify') && roleData?.role === 'therapist') {
            navigate(savedRedirectPath, { replace: true });
            return;
          } else if (savedRedirectPath.startsWith('/journal') && roleData?.role === 'client') {
            navigate(savedRedirectPath, { replace: true });
            return;
          }
        }
        
        // Only proceed with role-based redirects if no saved path exists or path is not accessible
        if (redirectTo === 'therapists' && (roleData?.role === 'client' || roleData?.role === 'therapist')) {
          console.log("Redirecting to therapists list");
          navigate("/therapists");
        } else if (roleData?.role === 'therapist') {
          console.log("Redirecting to therapist dashboard");
          navigate("/therapist");
        } else if (roleData?.role === 'client') {
          console.log("Redirecting to journal");
          navigate("/journal");
        } else if (roleData?.role === 'admin') {
          console.log("Redirecting to admin dashboard");
          navigate("/admin");
        } else {
          // If role isn't recognized, redirect to home
          console.log("Role not recognized, going to home page");
          navigate("/auth");
        }
      } catch (error) {
        console.error("Error in auth callback:", error);
        toast.error("Authentication error");
        navigate("/auth");
      } finally {
        setIsProcessing(false);
      }
    };

    handleAuthCallback();
  }, [navigate, location.search, session, sessionLoading]);

  if (sessionLoading || isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl">Processing authentication...</p>
      </div>
    );
  }

  // This will rarely be seen as we navigate away in the effect
  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-xl">Authentication complete...</p>
    </div>
  );
};

export default AuthCallback;
